import { toast } from "react-toastify"
import { getAccessToken } from "../../../../services/authService"
import { autoPaginate } from "./pageUtils"
import { getSceneLengths } from "./sceneutils"

// Scene lengths (eighths) are measured only after pagination, while the editor is visible.
const measuredLengths = (editor) => { try { return getSceneLengths(editor) } catch (e) { return null } }

// Lays out pages with the editor's own pagination (same result as its normal re-layout) and
// returns the page count for this exact content; null when the editor isn't visible to measure.
const paginatedPageCount = (editor) => {
    try {
        const body = editor?.getBody?.()
        const container = editor?.getContainer?.()
        // getClientRects (not offsetParent) so editors inside fixed-position layouts still count.
        if (!body || !container || container.getClientRects().length === 0) return null
        autoPaginate(editor)
        return body.querySelectorAll('.custom-pagebreak').length + 1
    } catch (e) { return null }
}

// After the editor finishes laying out pages on open: store the page count even when nothing
// was edited. Unchanged content sends only the count (server verifies it against the stored
// script); content changed by re-layout is saved normally. Returns the content now saved.
export const savePageCountOnOpen = async (editor, scriptData, scenes = 0) => {
    const pageCount = paginatedPageCount(editor)
    if (!pageCount || !scriptData?._id) return null
    const content = editor.getContent()
    if (content === (scriptData.content || '')) {
        const sceneLengths = measuredLengths(editor)
        await saveScript(sceneLengths ? { pageCount, sceneLengths } : { pageCount }, scriptData._id, () => { })
    } else {
        await saveContent(editor, scriptData, () => { }, scenes)
    }
    return content
}

export const saveContent = async (editor, scriptData, onComplete = () => { }, scenes = 0, newContent, options = {}) => {
    if (!newContent && !editor ) {
        console.error("content not found")
        return
    }
    //cannot save empty conent
    const pageCount = editor ? paginatedPageCount(editor) : null
    const content = editor?.getContent() || newContent || '<p></p>'
    const { author,
        basedOn,
        copyright,
        episodeNumber,
        episodeTitle,
        status,
        title,
        type, ...rest } = scriptData

    const data = {
        author,
        basedOn,
        content,
        copyright,
        episodeNumber,
        episodeTitle,
        status,
        scenes,
        title,
        type
    }
    if (pageCount) {
        data.pageCount = pageCount
        const sceneLengths = measuredLengths(editor)
        if (sceneLengths) data.sceneLengths = sceneLengths
    }
    // Server time this copy was loaded: lets the server keep AI Production items added since then.
    if (scriptData.updatedAt) data.loadedAt = scriptData.updatedAt
    return saveScript(data, scriptData._id, onComplete, options)
}

export const updateStatus = async (scriptData, status, onComplete = () => { }) => {
    const { author,
        basedOn,
        copyright,
        episodeNumber,
        episodeTitle,
        content,
        title,
        type, ...rest } = scriptData

    const data = {
        author,
        basedOn,
        status,
        copyright,
        episodeNumber,
        episodeTitle,
        status,
        title,
        type
    }
    await saveScript(data, scriptData._id, onComplete)
}

// Serialize writes per screenplay so a forced media save cannot race an older autosave.
const saveQueues = new Map()
export const saveScript = (data, id, onComplete, { strict = false } = {}) => {
    const previous = saveQueues.get(id) || Promise.resolve()
    const task = previous.catch(() => {}).then(async () => {
        try {
            const token = getAccessToken()
            if (!token) throw new Error('A valid session not found')
            const res = await fetch(`${process.env.REACT_APP_DEV_API}/document/${id}`, {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                method: 'PUT', body: JSON.stringify(data)
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.message || 'The screenplay could not be saved. Nothing was generated.')
            onComplete?.()
            return body
        } catch (error) {
            if (strict) throw error
            console.error(error)
            return null
        }
    })
    saveQueues.set(id, task)
    task.finally(() => { if (saveQueues.get(id) === task) saveQueues.delete(id) }).catch(() => {})
    return task
}

export const handleFileInput = async (e) => {

    const file = e.target.files[0]
    const formData = new FormData()
    formData.append('file', file)

    const token = getAccessToken()
    const url = `${process.env.REACT_APP_DEV_API}/import-document`
    try {
        if (!token) {
            throw new Error('A valid session not found')
        }

        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        })
        const body = await res.json()
        if (!res.ok) {
            console.error(res)
            throw new Error(body.message ? body.message : 'Error uploading file')
        }
        toast.success('File imported sucessfully')
        return body

    } catch (error) {
        toast.error(error.message)
    }
}

export const saveScriptAs = async (scriptData, title, onComplete = () => { }) => {
    const { author,
        basedOn,
        copyright,
        episodeNumber,
        episodeTitle,
        content,
        byline,
        scenes,
        status,
        type, ...rest } = scriptData

    const data = {
        author,
        basedOn,
        status,
        byline,
        copyright,
        episodeNumber,
        episodeTitle,
        content,
        scenes,
        title,
        type
    }
    await createScript(data, onComplete)
}

const createScript = async (data, onComplete) => {
    const token = getAccessToken()
    const url = `${process.env.REACT_APP_DEV_API}/document`
    try {
        if (!token) {
            throw new Error('A valid session not found')
        }
        const res = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            method: 'POST',
            body: JSON.stringify(data)

        })
        const body = await res.json()
        if (!res.ok) {
            console.error('res', res, body)
            throw new Error('')
        }
        onComplete()

    } catch (error) {
        toast.error('Failed to create script')
        console.error(error)
    }
}
export const editFindAndReplace = () => {
    const replaceNotificationStringWithWord = () => {
        const notification = document.querySelector(".tox-notification__body");
        const text = notification?.textContent
        if (text) {
            notification.textContent = text?.replace("string", "word")
        }
    }
    const findbutton = document.querySelector(".tox-dialog__footer-end");

    if (findbutton) {
        const handleClick = () => {
            setTimeout(() => {
                replaceNotificationStringWithWord();
            }, 0);
        };

        findbutton.addEventListener("click", handleClick);
        findbutton.addEventListener("touchstart", handleClick);
        findbutton.addEventListener("touchend", handleClick);
    }
}

export const parseToBody = (htmlString) => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return doc.body;
};

// Take body element and return string of child nodes
export const bodyToString = (body) => {
    return Array.from(body.children)
        .map(node => node.outerHTML)
        .join('\n');
};
